/**
 * OAuth Callback Handler
 * This page handles OAuth redirects when the funnel_id gets lost in the OAuth flow.
 * It reads the original funnel_id from sessionStorage and redirects accordingly.
 */
import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

const OAuthCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasRun = useRef(false);
  
  useEffect(() => {
    // Guard against React.StrictMode double-invocation
    if (hasRun.current) return;
    hasRun.current = true;
    
    const provider = searchParams.get('provider');
    const success = searchParams.get('success') === 'true';
    const error = searchParams.get('error');
    
    // Read the original funnel info from sessionStorage
    const funnelId = sessionStorage.getItem('oauth_return_funnel');
    const step = sessionStorage.getItem('oauth_return_step') || '6';
    const savedProvider = sessionStorage.getItem('oauth_platform');
    
    // Clear sessionStorage
    sessionStorage.removeItem('oauth_return_funnel');
    sessionStorage.removeItem('oauth_return_step');
    sessionStorage.removeItem('oauth_platform');
    
    if (error) {
      toast.error(`Error de OAuth: ${error}`);
      if (funnelId) {
        navigate(`/funnel/${funnelId}?step=${step}&oauth_error=${error}`);
      } else {
        navigate('/dashboard');
      }
      return;
    }
    
    if (success && funnelId) {
      // Redirect to the funnel with the appropriate flags
      const platform = provider || savedProvider;
      let redirectUrl = `/funnel/${funnelId}?step=${step}`;
      
      if (platform === 'google_ads') {
        redirectUrl += '&google_ads_connected=true&select_ad_account=true';
        toast.success('Google Ads conectado correctamente');
      } else if (platform === 'meta') {
        redirectUrl += '&meta_connected=true&select_ad_account=true';
        toast.success('Meta conectado correctamente');
      } else if (platform === 'ghl') {
        redirectUrl += '&ghl_connected=true';
        toast.success('GoHighLevel conectado correctamente');
      }
      
      navigate(redirectUrl);
    } else if (success) {
      // No funnel_id but success - go to dashboard with message
      toast.success('Integración conectada correctamente');
      navigate('/dashboard');
    } else {
      // Unknown state - go to dashboard
      navigate('/dashboard');
    }
  }, [navigate, searchParams]);
  
  return (
    <div className="min-h-screen bg-app-bg flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4"></div>
        <p className="text-text-muted">Procesando conexión...</p>
      </div>
    </div>
  );
};

export default OAuthCallback;
